import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, Plus, Trash2, Save } from "lucide-react";
import { api } from "@/lib/api";

const DAYS = [["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"], ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"]];

function HoursRow({ day, label, hours, onChange }) {
  const on = !!hours;
  return (
    <div className="flex flex-wrap items-center gap-3 py-2 border-b border-slate-800/60" data-testid={`schedule-day-${day}`}>
      <label className="flex items-center gap-2 w-32 text-sm">
        <input type="checkbox" checked={on} onChange={(e) => onChange(e.target.checked ? ["09:00", "19:00"] : null)} data-testid={`schedule-on-${day}`} className="accent-cyan-400" />
        <span className={on ? "text-slate-100" : "text-slate-500"}>{label}</span>
      </label>
      {on ? (
        <div className="flex items-center gap-2 text-sm">
          <input type="time" value={hours[0]} onChange={(e) => onChange([e.target.value, hours[1]])} className="dg-input w-32 py-1.5 text-sm" data-testid={`schedule-start-${day}`} />
          <span className="text-slate-500">to</span>
          <input type="time" value={hours[1]} onChange={(e) => onChange([hours[0], e.target.value])} className="dg-input w-32 py-1.5 text-sm" data-testid={`schedule-end-${day}`} />
        </div>
      ) : <span className="text-xs text-slate-500">Closed</span>}
    </div>
  );
}

function TimeOffList({ items, onChange }) {
  const [draft, setDraft] = useState({ date: "", start: "13:00", end: "17:00", reason: "" });
  const add = () => {
    if (!draft.date || draft.start >= draft.end) return toast.error("Pick a date and a start time before the end time");
    onChange([...items, draft]); setDraft({ date: "", start: "13:00", end: "17:00", reason: "" });
  };
  return (
    <div data-testid="schedule-timeoff">
      <div className="dg-eyebrow mb-2">Time off (install jobs, appointments)</div>
      {items.map((t, i) => (
        <div key={`${t.date}-${t.start}-${i}`} className="flex flex-wrap items-center gap-2 text-sm py-1" data-testid={`timeoff-row-${i}`}>
          <span className="font-mono text-cyan-300">{t.date}</span><span>{t.start}–{t.end}</span><span className="text-slate-400">{t.reason}</span>
          <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="dg-link text-xs ml-auto flex items-center gap-1" data-testid={`timeoff-remove-${i}`}><Trash2 className="w-3 h-3" /> remove</button>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-2 mt-2">
        <input type="date" value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} className="dg-input w-40 py-1.5 text-sm" data-testid="timeoff-date" />
        <input type="time" value={draft.start} onChange={(e) => setDraft({ ...draft, start: e.target.value })} className="dg-input w-28 py-1.5 text-sm" data-testid="timeoff-start" />
        <input type="time" value={draft.end} onChange={(e) => setDraft({ ...draft, end: e.target.value })} className="dg-input w-28 py-1.5 text-sm" data-testid="timeoff-end" />
        <input placeholder="Reason (optional)" value={draft.reason} onChange={(e) => setDraft({ ...draft, reason: e.target.value })} className="dg-input flex-1 min-w-[140px] py-1.5 text-sm" data-testid="timeoff-reason" />
        <button type="button" onClick={add} className="dg-btn-ghost rounded-full px-3 py-1.5 text-xs flex items-center gap-1" data-testid="timeoff-add"><Plus className="w-3 h-3" /> Block time</button>
      </div>
    </div>
  );
}

function DaysOff({ items, onChange }) {
  const [draft, setDraft] = useState("");
  return (
    <div data-testid="schedule-daysoff">
      <div className="dg-eyebrow mb-2">Days off</div>
      <div className="flex flex-wrap gap-2 items-center">
        {items.map((d) => <span key={d} className="dg-chip" data-testid={`dayoff-${d}`}>{d} <button type="button" onClick={() => onChange(items.filter((x) => x !== d))} className="ml-1 text-slate-400 hover:text-white" data-testid={`dayoff-remove-${d}`}>×</button></span>)}
        <input type="date" value={draft} onChange={(e) => setDraft(e.target.value)} className="dg-input w-40 py-1.5 text-sm" data-testid="dayoff-date" />
        <button type="button" onClick={() => { if (draft && !items.includes(draft)) onChange([...items, draft].sort()); setDraft(""); }} className="dg-btn-ghost rounded-full px-3 py-1.5 text-xs flex items-center gap-1" data-testid="dayoff-add"><Plus className="w-3 h-3" /> Add day off</button>
      </div>
    </div>
  );
}

export function AdminSchedule() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/schedule").then((r) => setS(r.data)).catch(() => toast.error("Couldn't load the schedule")); }, []);
  if (!s) return null;
  const save = async () => {
    setBusy(true);
    try { const r = await api.put("/admin/schedule", s); setS(r.data); toast.success("Schedule saved — the booking page updates instantly"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };
  const num = (k, lo, hi) => (e) => setS({ ...s, [k]: Math.min(hi, Math.max(lo, Number(e.target.value) || 0)) });

  return (
    <div className="dg-card rounded-xl p-5" data-testid="admin-schedule">
      <div className="flex items-center gap-2">
        <CalendarDays className="w-4 h-4 text-cyan-400" />
        <div className="dg-eyebrow">Your calendar · hours customers can book</div>
        <span className="dg-chip ml-auto text-xs">Pacific time</span>
      </div>
      <p className="text-sm text-slate-400 mt-2">Sessions are offered every {s.slot_step_min} minutes inside these hours, skipping anything already booked plus a {s.buffer_min}-minute breather after each session.</p>
      <div className="mt-4">
        {DAYS.map(([d, label]) => <HoursRow key={d} day={d} label={label} hours={s.hours[d]} onChange={(h) => setS({ ...s, hours: { ...s.hours, [d]: h } })} />)}
      </div>
      <div className="grid sm:grid-cols-3 gap-3 mt-4 text-sm">
        <label className="block"><div className="dg-eyebrow mb-1">Buffer between sessions (min)</div><input type="number" min="0" max="120" step="5" value={s.buffer_min} onChange={num("buffer_min", 0, 120)} className="dg-input py-1.5" data-testid="schedule-buffer" /></label>
        <label className="block"><div className="dg-eyebrow mb-1">Slot grid (min)</div><input type="number" min="15" max="120" step="15" value={s.slot_step_min} onChange={num("slot_step_min", 15, 120)} className="dg-input py-1.5" data-testid="schedule-step" /></label>
        <label className="block"><div className="dg-eyebrow mb-1">Earliest booking (hours from now)</div><input type="number" min="0" max="168" value={s.lead_hours} onChange={num("lead_hours", 0, 168)} className="dg-input py-1.5" data-testid="schedule-lead" /></label>
      </div>
      <div className="mt-5 space-y-5">
        <DaysOff items={s.days_off} onChange={(days_off) => setS({ ...s, days_off })} />
        <TimeOffList items={s.time_off} onChange={(time_off) => setS({ ...s, time_off })} />
      </div>
      <button type="button" onClick={save} disabled={busy} className="dg-btn-primary rounded-full px-5 py-2 text-sm flex items-center gap-2 mt-5 disabled:opacity-50" data-testid="schedule-save"><Save className="w-4 h-4" /> {busy ? "Saving…" : "Save schedule"}</button>
    </div>
  );
}
